import React from 'react';
import {Link,useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBuilding,FiClock,FiShield,FiCheck,FiPhone,FiCalendar,FiStar,FiUser,FiHome,FiUsers,FiArrowRight}=FiIcons;

const CommercialPage=()=> {
  const {area}=useParams();
  const currentArea=area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick=()=> {
    window.scrollTo({top: 0,behavior: 'smooth'});
  };

  const services=[ 
    {
      title: "Communal Cleaning",
      description: "Flats, apartments and retirement properties",
      image: "/img/communal-cleaning.webp",
      link: "/communal-cleaning"
    },
    {
      title: "Office Cleaning",
      description: "Professional workspace maintenance",
      image: "/img/commerial-homepage.webp",
      link: "/office-cleaning"
    },
    {
      title: "Education & Government",
      description: "Schools, colleges and public buildings",
      image: "/img/domestic-1.webp"
    },
    {
      title: "Retail Premises",
      description: "Shops, stores and commercial spaces",
      image: "/img/store.webp"
    },
    {
      title: "Bars & Restaurants",
      description: "Food service and hospitality cleaning",
      image: "/img/restaurant-cleaning1.webp",
      link: "/bars-restaurants-cleaning"
    },
    {
      title: "Deep/Spring Cleans",
      description: "Thorough one-off commercial cleaning",
      image: "/img/deep-cleaning.webp"
    },
    {
      title: "End of Tenancy",
      description: "Move-out cleaning for commercial properties",
      image: "/img/bedroom-cleaning.webp"
    },
    {
      title: "Showrooms & Marketing Suites",
      description: "Presentation-perfect commercial spaces",
      image: "/img/domestic-2.webp"
    }
  ];

  const features=[ 
    "Flexible scheduling to suit your business",
    "Fully trained and vetted cleaning staff",
    "Comprehensive insurance coverage",
    "Eco-friendly cleaning products available",
    "Emergency cleaning services",
    "Regular quality inspections",
    "Competitive commercial rates",
    "No long-term contracts required"
  ];

  const reviews=[ 
    {
      name: "David Johnson",
      company: "Property Management Ltd",
      rating: 5,
      text: `Diamond Cleaning has been maintaining our retirement property communal areas in ${currentArea} for 2 years. Residents consistently praise the cleanliness and the team's professionalism.`,
      date: "2 weeks ago"
    },
    {
      name: "Sarah Williams",
      company: "Tech Solutions Office",
      rating: 5,
      text: `Our office in ${currentArea} has never looked better! They clean every evening and we arrive to a spotless workspace. Highly professional service.`,
      date: "1 month ago"
    },
    {
      name: "Mark Thompson",
      company: "The Crown Restaurant",
      rating: 5,
      text: "Essential for our business! They understand the hygiene standards required in food service. Never let us down, even during busy periods.",
      date: "3 weeks ago"
    },
    {
      name: "Lisa Chen",
      company: "Retail Fashion Store",
      rating: 5,
      text: "Customer-facing areas always look immaculate. They work around our opening hours perfectly and maintain the highest standards.",
      date: "2 months ago"
    },
    {
      name: "Robert Clarke",
      company: "Apartment Complex Manager",
      rating: 5,
      text: `Managing multiple apartment buildings in ${currentArea}, reliability is key. Diamond Cleaning delivers consistent, quality service across all our properties.`,
      date: "1 week ago"
    },
    {
      name: "Emma Davis",
      company: "Educational Institution",
      rating: 5,
      text: "They understand the unique needs of educational environments. Safe products, flexible timing, and always professional with staff and students.",
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header currentArea={currentArea} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)),url('/img/communal-cleaning.webp')`
          }}
        />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Commercial Cleaning Services in {currentArea}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional commercial cleaning for offices, retail, hospitality, and residential properties. Reliable, thorough, and tailored to your business needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Get Quote Online</span>
              </Link>
              <a
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Commercial Cleaning Services in {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive commercial cleaning solutions for every type of business and property
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service,index)=> (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div 
                  className="h-48 bg-cover bg-center relative"
                  style={{backgroundImage: `url('${service.image}')`}}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  {service.link && (
                    <Link
                      to={service.link}
                      className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold"
                      onClick={handleLinkClick}
                    >
                      <span>Learn More</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Commercial Cleaning Services?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We understand that every business has unique cleaning requirements. Our experienced team provides flexible, reliable, and professional commercial cleaning services across {currentArea} and surrounding areas.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature,index)=> (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link
                  to="/book"
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  onClick={handleLinkClick}
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Request Quote</span>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div 
                className="rounded-2xl overflow-hidden shadow-2xl h-96 bg-cover bg-center"
                style={{backgroundImage: `url('/img/restaurant-cleaning1.webp')`}}
              >
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Specialized Commercial Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Detailed breakdown of our commercial cleaning expertise in {currentArea}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Property Management */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiBuilding} className="w-12 h-12 text-blue-600 mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Property Management</h3>
                  <p className="text-gray-600">Residential & Commercial Properties</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                  <span>Communal areas in flats and apartments</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                  <span>Retirement property maintenance</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                  <span>Stairwells, lobbies, and corridors</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                  <span>Lift cleaning and maintenance</span>
                </li>
              </ul>
            </div>

            {/* Business Services */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiUsers} className="w-12 h-12 text-green-600 mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Business Services</h3>
                  <p className="text-gray-600">Offices & Workspaces</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                  <span>Daily office cleaning services</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                  <span>Meeting rooms and reception areas</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                  <span>Washroom and kitchen facilities</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                  <span>Window cleaning and floor maintenance</span>
                </li>
              </ul>
            </div>

            {/* Retail & Hospitality */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiBuilding} className="w-12 h-12 text-purple-600 mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Retail & Hospitality</h3>
                  <p className="text-gray-600">Customer-Facing Businesses</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
                  <span>Retail stores and shopping centers</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
                  <span>Restaurant and bar cleaning</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
                  <span>Kitchen deep cleaning services</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
                  <span>Showrooms and marketing suites</span>
                </li>
              </ul>
            </div>

            {/* Specialized Services */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiShield} className="w-12 h-12 text-orange-600 mr-4" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Specialized Services</h3>
                  <p className="text-gray-600">Education & Government</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-orange-600" />
                  <span>Schools and educational facilities</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-orange-600" />
                  <span>Government and public buildings</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-orange-600" />
                  <span>End of tenancy deep cleans</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-orange-600" />
                  <span>Spring cleaning and maintenance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Elevate Your Business Environment?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Professional commercial cleaning services that keep your business in {currentArea} looking its best and running smoothly.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Flexible Scheduling</h3>
              <p className="text-blue-100">Work around your business hours</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Fully Insured</h3>
              <p className="text-blue-100">Complete peace of mind</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiCheck} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Quality Guaranteed</h3>
              <p className="text-blue-100">Consistent, professional results</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              onClick={handleLinkClick}
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Get Free Quote</span>
            </Link>
            <a
              href="tel:07878522378"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      {/* Commercial Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Commercial Cleaning Reviews in {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our commercial clients say about our professional cleaning services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review,index)=> (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-600">{review.company}</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(review.rating)].map((_,i)=> (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
                
                <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://share.google/xr3mZOVXjGPtaFHeP"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <span>Read More Reviews on Google</span>
            </a>
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <AreasSection currentArea={currentArea} />

      <Footer />
    </div>
  );
};

const formatAreaName=(area)=> {
  return area
    .split('-')
    .map(word=> word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default CommercialPage;